import { Box, Button, Flex, Spacer, Text } from '@chakra-ui/react';
import { Formik, getIn, setIn } from 'formik';
import _ from 'lodash';
import React, { useState } from 'react';

import { CreditAgreementFullArtifactWipConfigType } from '../../types';
import { EditButton } from './EditButton';
import { colors } from 'packages/common-frontend/colors';
import {
  InputFormField,
  TextareaFormField,
} from 'packages/common-frontend/components/FormField';
import {
  SaveIconButton,
  XIconButton,
} from 'packages/common-frontend/components/IconButton';
import { CreditAgreement } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';

export type EditPropertyFieldOnSave = (
  props: any,
) => CreditAgreement.DomainEntity.CreditAgreementFullArtifact;

export type EditPropertyFieldInputType = 'input' | 'textArea';

export const EditablePropertyField = ({
  basePropertyKey,
  fullPropertyKey,
  wipConfig,
  label,
  inputType = 'input',
  isEditDisabled,
}: {
  basePropertyKey: string;
  fullPropertyKey: string;
  wipConfig: CreditAgreementFullArtifactWipConfigType;
  label?: string;
  inputType?: EditPropertyFieldInputType;
  isEditDisabled?: boolean;
}) => {
  const [isEditMode, setIsEditMode] = useState<boolean>(false);

  const { formProps, saveCreditAgreementFullArtifactWip } = wipConfig;

  const currentProperty = getIn(formProps.values, fullPropertyKey);

  return (
    <Formik
      onSubmit={(props) => {
        const wipCreditAgreement = formProps.values;

        // set to change setFieldValue key
        formProps.setFieldValue(fullPropertyKey, props);

        const updatedWipCreditAgreement = setIn(
          wipCreditAgreement,
          fullPropertyKey,
          props,
        );

        saveCreditAgreementFullArtifactWip({
          variables: {
            creditAgreementFullArtifactWip: {
              id: formProps.values.id,
              creditFacilityId: formProps.values.creditFacilityId,
              creditAgreement: JSON.stringify(updatedWipCreditAgreement),
            },
          },
        });

        setIsEditMode(false);
      }}
      initialValues={{
        ...currentProperty,
      }}
    >
      {(localFormProps) => {
        return (
          <Box mb="24px">
            {!isEditMode && (
              <>
                {label ? (
                  <>
                    <Flex alignItems="center" width="100%">
                      <Text fontWeight={500}>{label}</Text>
                      <Spacer />
                      {!isEditDisabled && (
                        <EditButton setIsOpen={setIsEditMode} />
                      )}
                    </Flex>
                    <Text color={colors.black[70]} overflowWrap="anywhere">
                      {currentProperty[basePropertyKey] ?? '-'}
                    </Text>
                  </>
                ) : (
                  <>
                    <Flex alignItems="top">
                      <Text
                        color={colors.black[70]}
                        overflowWrap="anywhere"
                        pr="8px"
                      >
                        {currentProperty[basePropertyKey] ?? '-'}
                      </Text>
                      <Spacer />
                      {!isEditDisabled && (
                        <EditButton setIsOpen={setIsEditMode} />
                      )}
                    </Flex>
                  </>
                )}
              </>
            )}
            {isEditMode && !isEditDisabled && (
              <>
                {label && (
                  <Text fontWeight={500} mb="16px">
                    {label}
                  </Text>
                )}
                <Flex alignItems="center">
                  {inputType === 'input' && (
                    <InputFormField
                      propName={basePropertyKey}
                      label=""
                      formProps={localFormProps}
                      value={getIn(localFormProps.values, basePropertyKey)}
                      placeholder={`Enter ${label}`}
                    />
                  )}
                  {inputType === 'textArea' && (
                    <TextareaFormField
                      propName={basePropertyKey}
                      label=""
                      formProps={localFormProps}
                      value={getIn(localFormProps.values, basePropertyKey)}
                      placeholder={`Enter ${label}`}
                    />
                  )}

                  {label ? (
                    <>
                      <Button
                        aria-label="Close"
                        onClick={() => {
                          localFormProps.setFieldValue(
                            basePropertyKey,
                            currentProperty[basePropertyKey],
                          );

                          setIsEditMode(false);
                        }}
                        colorScheme="aurora"
                        variant="outline"
                        borderWidth="2px"
                        mr="24px"
                        ml="32px"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={() => localFormProps.handleSubmit()}
                        aria-label="Save"
                        disabled={_.keys(formProps.errors).length > 0}
                        colorScheme="aurora"
                        color="white"
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <>
                      <SaveIconButton
                        label="Save"
                        ariaLabel="save"
                        onClick={() => localFormProps.handleSubmit()}
                      />
                      <XIconButton
                        label="Close"
                        ariaLabel="close"
                        onClick={() => {
                          localFormProps.setFieldValue(
                            basePropertyKey,
                            currentProperty[basePropertyKey],
                          );

                          setIsEditMode(false);
                        }}
                      />
                    </>
                  )}
                </Flex>
              </>
            )}
          </Box>
        );
      }}
    </Formik>
  );
};
